import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API });

const CART_KEY = "zoya_cart";
const WISH_KEY = "zoya_wishlist";

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const StoreContext = createContext(null);

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => read(CART_KEY, []));
  const [wishlist, setWishlist] = useState(() => read(WISH_KEY, []));
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => localStorage.setItem(CART_KEY, JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem(WISH_KEY, JSON.stringify(wishlist)), [wishlist]);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const found = prev.find((i) => i.slug === product.slug);
      if (found)
        return prev.map((i) => (i.slug === product.slug ? { ...i, qty: i.qty + qty } : i));
      return [
        ...prev,
        {
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.images?.[0],
          qty,
        },
      ];
    });
    toast.success(`${product.name} masuk daftar pilihan`);
  }, []);

  const updateQty = useCallback((slug, qty) => {
    setCart((prev) =>
      qty <= 0
        ? prev.filter((i) => i.slug !== slug)
        : prev.map((i) => (i.slug === slug ? { ...i, qty } : i))
    );
  }, []);

  const removeFromCart = useCallback((slug) => {
    setCart((prev) => prev.filter((i) => i.slug !== slug));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    const slug = product.slug;
    setWishlist((prev) => {
      const exists = prev.includes(slug);
      toast.success(exists ? "Dihapus dari favorit" : `${product.name} disimpan di favorit`);
      return exists ? prev.filter((s) => s !== slug) : [...prev, slug];
    });
  }, []);

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);

  const value = {
    cart,
    cartCount,
    subtotal,
    addToCart,
    updateQty,
    removeFromCart,
    clearCart,
    wishlist,
    toggleWishlist,
    searchOpen,
    setSearchOpen,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => useContext(StoreContext);
